import React, { ChangeEventHandler, ReactNode } from 'react';
import styled from 'styled-components';

import { BaseForm } from 'shared/types';
import { TRANSITIONS } from 'shared/constants/theme';
import pxInRem from 'shared/helpers/styled-components/remHelper';

const Wrapper = styled.div<{
  disabled?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-m);
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const Input = styled.input.attrs({ type: 'checkbox' })<Props>`
  appearance: none;
  position: relative;
  cursor: pointer;
  width: 18px;
  height: 18px;
  border-radius: var(--border-radius-xxs);
  margin: 6px;

  &:before {
    transition: transform 0.4s ${TRANSITIONS.bounce};
    transform: rotate(-45deg) scale(0, 0);

    content: '';
    position: absolute;
    top: ${() => 0.2 * 18}px;
    left: ${() => 0.25 * 18}px;
    width: ${() => 0.5 * 18}px;
    height: ${() => 0.35 * 18}px;

    border: 2px solid var(--icons-default);
    border-top-style: none;
    border-right-style: none;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    border-radius: var(--border-radius-xxs);
    border: 2px solid
      ${props => (props.error ? 'var(--semantic-error)' : 'var(--icons-black)')};
  }

  ${props =>
    props.checked &&
    `
    background: var(--icons-black);
    
    &:before {
      transform: rotate(-45deg) scale(1, 1);
    }
    &:after {
      border-color: ${
        props.error ? 'var(--semantic-error)' : 'var(--icons-black)'
      };
    }
  `}
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  font-size: ${pxInRem(16)};
  line-height: ${pxInRem(23)};

  &:hover {
    color: var(--text-default);
  }
`;

const Children = styled.span`
  color: var(--text-light);
`;

export interface Props extends BaseForm {
  id: string;
  value?: string;
  label?: string | ReactNode;
  checked: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  children?: ReactNode;
}

export default function Checkbox({
  id,
  name,
  value,
  checked,
  label,
  onChange,
  disabled,
  className,
  error,
  children
}: Props) {
  return (
    <Wrapper className={className} disabled={disabled}>
      <Input
        id={id}
        name={name}
        value={value || name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        error={error}
      />
      <Label htmlFor={id}>
        <span>{label}</span>
        {children && <Children>{children}</Children>}
      </Label>
    </Wrapper>
  );
}
